import { useState } from "react";
import { getErrorMessage } from "@/lib/errors";
import { useNavigate } from "react-router-dom";
import { authClient } from "@/services/auth";
import { ForgotPasswordForm } from "@/components/Auth/ForgotPasswordForm";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

// Demo credentials live as Supabase function secrets and are exchanged for a
// session by the `demo-login` edge function. Anything read from import.meta.env
// is inlined into the client bundle, so the password must never be a VITE_ var.
const DEMO_ENABLED = import.meta.env.VITE_DEMO_ENABLED === "true";

export const LoginForm = () => {
  // Which of the two things this card is showing. "forgot" swaps the login fields for the
  // email request form and back, in place, with no page reload (FR-001).
  const [mode, setMode] = useState<"login" | "forgot">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const runSignIn = async (signInEmail: string, signInPassword: string) => {
    setLoading(true);
    try {
      const { error } = await authClient.signIn(signInEmail, signInPassword);
      if (error) throw error;
      toast({
        title: "Success",
        description: "Logged in successfully",
      });
      navigate("/app");
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    await runSignIn(email, password);
  };

  const handleDemo = async () => {
    if (!DEMO_ENABLED) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("demo-login");
      if (error) throw error;
      if (!data?.access_token || !data?.refresh_token) {
        throw new Error("Demo login is not available right now");
      }
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      });
      if (sessionError) throw sessionError;
      toast({
        title: "Success",
        description: "Logged in to the demo account",
      });
      navigate("/app");
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (mode === "forgot") {
    return <ForgotPasswordForm onBack={() => setMode("login")} />;
  }

  return (
    <form onSubmit={handleLogin} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <div className="text-right">
          <Button
            type="button"
            variant="link"
            onClick={() => setMode("forgot")}
            className="h-auto p-0 text-sm text-muted-foreground"
          >
            Forgot password?
          </Button>
        </div>
      </div>
      <Button type="submit" className="w-full" disabled={loading}>
        {loading ? "Logging in..." : "Log In"}
      </Button>
      {DEMO_ENABLED && (
        <div className="text-center">
          <Button
            type="button"
            variant="link"
            onClick={handleDemo}
            disabled={loading}
            className="text-sm text-muted-foreground"
          >
            Try the demo →
          </Button>
        </div>
      )}
    </form>
  );
};
